import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { InteractionManager, View } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import { partition, uniqBy } from 'lodash'
import { useRoute, RouteProp, useNavigation } from '@react-navigation/core'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { StyleService, useStyleSheet } from '@src/style/service'
import { IconButton } from '@components/base'
import { SearchBar, TabView } from '@components'
import { AppStackParamList } from '@navigation/types'
import { NavigationContainer } from '@screens/Common/containers'
import {
  useCustomIngredients,
  useIngredientSearch,
  useRecentIngredientsForMealType,
} from '@screens/Ingredients/utils/hooks'
import { BaseIngredient } from '@screens/Ingredients/types'
import { getFullNutritionixData, normalizeNutritionixItem } from '@screens/Ingredients/utils'
import { SceneContent } from '@screens/Ingredients/components/SceneContent'
import { mealDraftSelector } from '@selectors/meals'
import { IngredientSource } from '@src/types'
import { useLazyScreenPreload } from '@src/navigation/utils'

export const IngredientSearch = () => {
  const styles = useStyleSheet(themedStyles)
  const navigation = useNavigation()
  const insets = useSafeAreaInsets()
  const dispatch = useDispatch()

  const { params } = useRoute<RouteProp<AppStackParamList, 'IngredientSearch'>>()
  const { createdIngredientId, mealType } = params

  const [query, setQuery] = useState('')
  const [tabIndex, setTabIndex] = useState(0)

  const { ingredients: addedIngredients = [] } = useSelector(mealDraftSelector)

  const { ingredients, isLoading } = useIngredientSearch(query)
  const customIngredients = useCustomIngredients(query)
  const recentIngredients = useRecentIngredientsForMealType(mealType, query)

  const removeIngredient = useCallback(
    (ingredient: BaseIngredient) => {
      dispatch({
        type: 'meals/removeDraftIngredient',
        payload: ingredient,
      })
    },
    [dispatch],
  )

  const allIngredients = useMemo(
    () => uniqBy([...recentIngredients, ...customIngredients, ...ingredients], 'description'),
    [customIngredients, ingredients, recentIngredients],
  )

  const allThirdPartyIngredients = useMemo(
    () =>
      uniqBy(
        [...recentIngredients, ...ingredients].filter((item) => item.thirdPartyIngredientId),
        'thirdPartyIngredientId',
      ),
    [ingredients, recentIngredients],
  )

  const [branded, common] = useMemo(
    () =>
      partition(allThirdPartyIngredients, (item) =>
        item.thirdPartyIngredientId?.startsWith('branded'),
      ),
    [allThirdPartyIngredients],
  )

  const recentIngredientsPositionMap = useMemo(() => {
    return new Map(recentIngredients.map((ingredient, index) => [ingredient.description, index]))
  }, [recentIngredients])

  const sortedCustomIngredients = useMemo(() => {
    return [...customIngredients].sort((a, b) => {
      const aIndex = recentIngredientsPositionMap.get(a.description) ?? -1
      const bIndex = recentIngredientsPositionMap.get(b.description) ?? -1

      // If both are in recent ingredients, sort by their order in recent ingredients
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex
      }

      // If only one is in recent ingredients, prioritize it
      if (aIndex !== -1) {
        return -1
      }
      if (bIndex !== -1) {
        return 1
      }

      // If neither is in recent ingredients, maintain original order
      return 0
    })
  }, [recentIngredientsPositionMap, customIngredients])

  const onItemSelect = useCallback(
    async (item: BaseIngredient) => {
      let ingredient = item

      if (item.thirdPartyIngredientSource === IngredientSource.Nutritionix) {
        const data = await getFullNutritionixData([item])

        const ingredientWithNutrients = data[0] as typeof data[0] | undefined

        if (!ingredientWithNutrients) {
          return
        }

        ingredient = {
          ...normalizeNutritionixItem(ingredientWithNutrients),
          servingAmount: item.servingAmount,
          servingUnits: item.servingUnits,
        }
      }

      navigation.navigate('IngredientPortion', { ingredient })
    },
    [navigation],
  )

  const onRemovePress = useCallback(
    (ingredient: BaseIngredient) => {
      removeIngredient(ingredient)
    },
    [removeIngredient],
  )

  const contentContainerStyle = useMemo(() => ({ paddingBottom: insets.bottom }), [insets.bottom])

  const createdIngredient = customIngredients.find(
    (ingredient) => ingredient.id === createdIngredientId,
  )

  useEffect(() => {
    if (createdIngredient) {
      // Defer navigation until the screen transition animation completes.
      // Navigating immediately can race with TabView's animated component setup.
      const handle = InteractionManager.runAfterInteractions(() => {
        navigation.setParams({ createdIngredientId: undefined })
        navigation.navigate('IngredientPortion', { ingredient: createdIngredient })
      })
      return () => handle.cancel()
    }
  }, [createdIngredient, navigation])

  useLazyScreenPreload('BarcodeIngredientSearch')

  const onBarcodeIngredientSearchPress = useCallback(() => {
    navigation.navigate('BarcodeIngredientSearch')
  }, [navigation])

  const staticHeader = useMemo(
    () => (
      <View style={styles.header}>
        <SearchBar
          autoFocus
          testID="IngredientSearch/SearchBar"
          style={styles.searchBar}
          value={query}
          onChangeText={setQuery}
        />
        <IconButton
          accessibilityLabel="Scan Barcode"
          iconName="barcode"
          shape="square"
          size="m"
          type="transparent"
          onPress={onBarcodeIngredientSearchPress}
        />
      </View>
    ),
    [styles.header, styles.searchBar, query, onBarcodeIngredientSearchPress],
  )

  const routes = useMemo(
    () => [
      {
        key: 'all',
        title: 'All',
        sceneComponent: (
          <SceneContent
            ingredients={allIngredients}
            isLoading={isLoading}
            query={query}
            addedIngredients={addedIngredients}
            onItemSelect={onItemSelect}
            onRemovePress={onRemovePress}
            contentContainerStyle={contentContainerStyle}
          />
        ),
      },
      {
        key: 'branded',
        title: 'Branded',
        sceneComponent: (
          <SceneContent
            ingredients={branded}
            isLoading={isLoading}
            query={query}
            addedIngredients={addedIngredients}
            onItemSelect={onItemSelect}
            onRemovePress={onRemovePress}
            contentContainerStyle={contentContainerStyle}
          />
        ),
      },
      {
        key: 'common',
        title: 'Common',
        sceneComponent: (
          <SceneContent
            ingredients={common}
            isLoading={isLoading}
            query={query}
            addedIngredients={addedIngredients}
            onItemSelect={onItemSelect}
            onRemovePress={onRemovePress}
            contentContainerStyle={contentContainerStyle}
          />
        ),
      },
      {
        key: 'custom',
        title: 'Custom',
        sceneComponent: (
          <SceneContent
            ingredients={sortedCustomIngredients}
            isCustomTab
            isLoading={isLoading}
            query={query}
            addedIngredients={addedIngredients}
            onItemSelect={onItemSelect}
            onRemovePress={onRemovePress}
            contentContainerStyle={contentContainerStyle}
          />
        ),
      },
    ],
    [
      allIngredients,
      branded,
      common,
      sortedCustomIngredients,
      isLoading,
      query,
      addedIngredients,
      onItemSelect,
      onRemovePress,
      contentContainerStyle,
    ],
  )

  return (
    <NavigationContainer title="Find Ingredients" style={styles.container}>
      <TabView
        tabIndex={tabIndex}
        onChangeTabIndex={setTabIndex}
        swipeEnabled
        staticHeader={staticHeader}
        routes={routes}
      />
    </NavigationContainer>
  )
}

const themedStyles = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: 8,
  },
  searchBar: {
    flex: 1,
    marginRight: 8,
  },
})
